// ─────────────────────────────────────────────────────────────────
//  PatientTopBar.jsx  —  Clinical Clarity top bar v3
//  • Page-identity block with gradient accent dot
//  • Date chip, refined icon buttons with press feedback
//  Props unchanged: { activeNav, onRefresh, onLogout, onMobileMenu, dependentSlot }
// ─────────────────────────────────────────────────────────────────
import React, { useState } from "react";
import { Flex, Tooltip, Typography } from "antd";
import {
  ReloadOutlined, LogoutOutlined, MenuOutlined, CalendarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import {
  SLATE, MUTED, GRAY6, GRAY7, GRAY2, G700, G50, G100,
  RED, RED_BG, RED_BORDER,
  GRAD_BRAND,
  FF_DISPLAY, FF_BODY, FF_MONO,
  RADIUS_SM, RADIUS_PILL, TRANSITION_SM,
} from "./patientTokens";

const { Text } = Typography;

const PAGE_META = {
  overview:          { title: "Overview",         sub: "Your health at a glance" },
  appointments:      { title: "Appointments",     sub: "Upcoming & past visits booked" },
  visits:            { title: "Visit History",    sub: "All your hospital visits" },
  "medical-records": { title: "Medical Records",  sub: "Consultation, vitals & prescriptions" },
  investigation:     { title: "Investigations",   sub: "Lab results & reports" },
  profile:           { title: "My Profile",       sub: "Your personal information" },
};

// ── Small round icon button ────────────────────────────────────
function IconBtn({ onClick, label, children }) {
  return (
    <Tooltip title={label} placement="bottom">
      <button
        onClick={onClick}
        aria-label={label}
        style={{
          width: 40, height: 40,
          borderRadius: RADIUS_SM,
          background: GRAY7,
          border: `1px solid transparent`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: GRAY2, fontSize: 15,
          transition: TRANSITION_SM,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = G50;
          e.currentTarget.style.borderColor = G100;
          e.currentTarget.style.color = G700;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = GRAY7;
          e.currentTarget.style.borderColor = "transparent";
          e.currentTarget.style.color = GRAY2;
        }}
        onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.94)")}
        onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        {children}
      </button>
    </Tooltip>
  );
}

function PatientTopBar({ activeNav, onRefresh, onLogout, onMobileMenu, dependentSlot }) {
  const meta = PAGE_META[activeNav] || { title: "Dashboard", sub: "" };
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    onRefresh?.();
    setTimeout(() => setRefreshing(false), 700);
  };

  return (
    <div className="pd-topbar">
      {/* ── Left: menu + page identity ─────────────────────── */}
      <Flex align="center" gap={14} style={{ minWidth: 0 }}>
        <button
          onClick={onMobileMenu}
          className="pd-hamburger"
          aria-label="Open menu"
          style={{
            display: "none",
            width: 40, height: 40,
            borderRadius: RADIUS_SM,
            background: GRAY7,
            border: "none",
            alignItems: "center", justifyContent: "center",
            color: GRAY2, fontSize: 16, flexShrink: 0,
            transition: TRANSITION_SM,
          }}
        >
          <MenuOutlined />
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          {/* Gradient accent dot ties the page title to the brand */}
          <span
            className="pd-topbar-dot"
            style={{
              width: 8, height: 30, borderRadius: 4,
              background: GRAD_BRAND, flexShrink: 0,
              boxShadow: "0 2px 8px rgba(14,91,194,0.35)",
            }}
          />
          <div style={{ minWidth: 0 }}>
            <Text style={{
              color: SLATE, fontFamily: FF_DISPLAY, fontWeight: 700,
              fontSize: 17, display: "block", lineHeight: 1.25,
              letterSpacing: "-0.4px",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {meta.title}
            </Text>
            <Text
              className="pd-topbar-sub"
              style={{
                color: MUTED, fontSize: 12, fontFamily: FF_BODY,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                display: "block",
              }}
            >
              {meta.sub || "Patient portal"}
            </Text>
          </div>
        </div>
      </Flex>

      {/* ── Right: date chip, refresh, dependent, sign out ──── */}
      <Flex gap={10} align="center" style={{ flexShrink: 0 }}>
        <span
          className="pd-topbar-date"
          style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            height: 34, padding: "0 13px",
            borderRadius: RADIUS_PILL,
            background: G50, border: `1px solid ${G100}`,
            color: G700, fontSize: 12, fontWeight: 600,
            fontFamily: FF_MONO, whiteSpace: "nowrap",
          }}
        >
          <CalendarOutlined style={{ fontSize: 12 }} />
          {dayjs().format("ddd, MMM D")}
        </span>

        <IconBtn onClick={handleRefresh} label="Refresh">
          <ReloadOutlined spin={refreshing} />
        </IconBtn>

        {dependentSlot}

        <button
          onClick={onLogout}
          style={{
            height: 40,
            borderRadius: RADIUS_SM,
            padding: "0 16px",
            background: RED_BG,
            border: `1px solid ${RED_BORDER}`,
            display: "flex", alignItems: "center", gap: 7,
            color: RED, fontSize: 13.5, fontWeight: 600,
            fontFamily: FF_BODY,
            transition: TRANSITION_SM,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#fee2e2";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(229,62,62,0.14)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = RED_BG;
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <LogoutOutlined style={{ fontSize: 13 }} />
          <span className="pd-topbar-logout-label">Sign Out</span>
        </button>
      </Flex>

      <style>{`
        @media (max-width: 768px) {
          .pd-hamburger { display: flex !important; }
          .pd-topbar-logout-label { display: none; }
          .pd-topbar-sub { display: none !important; }
          .pd-topbar-date { display: none !important; }
          .pd-topbar-dot { display: none !important; }
        }
      `}</style>
    </div>
  );
}

export default PatientTopBar;